import {
    AlertTriangle,
    Archive,
    ArrowRight,
    CheckCircle2,
    ChevronRight,
    LoaderCircle,
    Plus,
    RefreshCw,
    Sparkles,
    Users,
    Zap,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './IncentiveStructureScreen.module.css'

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseActiveStore = (raw) => {
    try {
        return raw && raw.trim() !== '' ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const TYPE_META = {
    SERVICE_COMMISSION:  { label: 'Service %',       color: '#1565c0', bg: '#e3f2fd' },
    RETAIL_COMMISSION:   { label: 'Retail %',         color: '#00695c', bg: '#e0f2f1' },
    CATEGORY_COMMISSION: { label: 'By Category',      color: '#6a1b9a', bg: '#f3e5f5' },
    SLAB_COMMISSION:     { label: 'Tiered Slabs',     color: '#e65100', bg: '#fff3e0' },
    TARGET_BONUS:        { label: 'Target Bonus',     color: '#2e7d32', bg: '#e8f5e9' },
    ATTENDANCE_BONUS:    { label: 'Attendance Bonus', color: '#283593', bg: '#e8eaf6' },
    CUSTOM_INCENTIVE:    { label: 'Custom',           color: '#546e7a', bg: '#eceff1' },
}

// ── Component ─────────────────────────────────────────────────────────────────
const IncentiveStructureScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [structureData, setStructureData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [showArchived, setShowArchived] = useState(false)

    // ── Fetch ─────────────────────────────────────────────────────
    const fetchIncentiveTemplateList = useCallback(
        async (includeArchived = false, { silent = false } = {}) => {
            if (!authCtx?.token || !storeIdentifier) {
                if (!silent) setIsLoading(false)
                return
            }
            if (!silent) setIsLoading(true)
            else setIsRefreshing(true)

            try {
                const response = await fetch(BASE_URL_API + 'getStoreIncentiveTemplateList', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier,
                        showArchived: includeArchived,
                    }),
                })

                if (!response.ok) {
                    toast.error('Failed to load commission plans.')
                    return
                }

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setStructureData(respo.response)
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                toast.error(respo?.message || 'Failed to load commission plans.')
            } catch (error) {
                console.error('fetchIncentiveTemplateList error:', error)
                toast.error('Network error. Please check your connection.')
            } finally {
                setIsLoading(false)
                setIsRefreshing(false)
            }
        },
        [authCtx, storeIdentifier]
    )

    // Initial + archive toggle
    useEffect(() => {
        fetchIncentiveTemplateList(showArchived)
    }, [fetchIncentiveTemplateList, showArchived])

    // Refresh on back-nav from create / detail screens
    useEffect(() => {
        if (!storeIdentifier) return
        fetchIncentiveTemplateList(showArchived, { silent: true })
    }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

    // Refresh on window focus / tab visibility
    useEffect(() => {
        const onFocus = () => {
            if (!storeIdentifier) return
            fetchIncentiveTemplateList(showArchived, { silent: true })
        }
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onFocus()
        }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [fetchIncentiveTemplateList, showArchived, storeIdentifier])

    // ── Derived ──────────────────────────────────────────────────
    const templates = useMemo(
        () => (Array.isArray(structureData?.templates) ? structureData.templates : []),
        [structureData]
    )
    const isEmpty = !isLoading && templates.length === 0

    // ── Navigation ───────────────────────────────────────────────
    const goToCreate = () => navigate('/payroll/setup/incentive-structure/create')

    const goToDetail = (templateIdentifier, templateName) => {
        navigate(`/payroll/incentive-structure/${templateIdentifier}`, {
            state: { templateIdentifier, templateName },
        })
    }

    // ─────────────────────────────────────────────────────────────
    return (
        <div className={styles.page}>

            {/* ── Hero ── */}
            <section className={styles.hero}>
                <div className={styles.heroTopRow}>
                    <span className={styles.heroPill}><Sparkles size={14} /> Commission Plans</span>
                    {isRefreshing && (
                        <span className={styles.refreshHint}>
                            <RefreshCw size={13} className={styles.spin} /> Refreshing…
                        </span>
                    )}
                </div>
                <div className={styles.heroMain}>
                    <div>
                        <h1>Incentive Structures</h1>
                        <p>
                            Define how variable pay is earned — commission percentages, target bonuses,
                            and attendance rewards. Assign a plan to each staff member alongside their salary structure.
                        </p>
                    </div>
                    <button type="button" className={styles.createBtn} onClick={goToCreate}>
                        <Plus size={15} /> Create Plan
                    </button>
                </div>
            </section>

            {/* ── No store guard ── */}
            {!storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select an active store to continue.</strong>
                        <p>Commission plans are store-specific.</p>
                    </div>
                </section>
            )}

            {/* ── Context info banner (always shown when loaded) ── */}
            {!isLoading && storeIdentifier && (
                <div className={styles.infoBanner}>
                    <Zap size={14} className={styles.infoBannerIcon} />
                    <p>
                        Commission is auto-computed from session revenue. This plan defines the rules.
                        Fixed salary is managed separately under <strong>Salary Structures</strong>.
                    </p>
                </div>
            )}

            {/* ── Summary row ── */}
            {structureData && (
                <section className={styles.summaryRow}>
                    <div className={styles.summaryPills}>
                        <span className={styles.summaryPillActive}>
                            <CheckCircle2 size={13} /> {structureData.activeCount} Active
                        </span>
                        {structureData.archivedCount > 0 && (
                            <span className={styles.summaryPillArchived}>
                                <Archive size={13} /> {structureData.archivedCount} Archived
                            </span>
                        )}
                    </div>
                    {(structureData.archivedCount > 0 || showArchived) && (
                        <button
                            type="button"
                            className={`${styles.archiveToggle} ${showArchived ? styles.archiveToggleOn : ''}`}
                            onClick={() => setShowArchived((prev) => !prev)}
                        >
                            {showArchived ? 'Hide Archived' : 'Show Archived'}
                        </button>
                    )}
                </section>
            )}

            {/* ── Loading ── */}
            {isLoading && (
                <section className={styles.loaderCard}>
                    <LoaderCircle size={22} className={styles.spin} />
                    <p>Loading commission plans…</p>
                </section>
            )}

            {/* ── Empty state ── */}
            {isEmpty && (
                <section className={styles.emptyState}>
                    <div className={styles.emptyIllo}>
                        <Zap size={36} className={styles.emptyIlloIcon} />
                    </div>
                    <h3>No commission plans yet</h3>
                    <p>
                        Create a plan to define how staff earn variable pay based on service revenue,
                        targets, and attendance.
                    </p>
                    <button type="button" className={styles.emptyCtaBtn} onClick={goToCreate}>
                        Create First Plan <ArrowRight size={15} />
                    </button>
                </section>
            )}

            {/* ── Template list ── */}
            {!isLoading && templates.length > 0 && (
                <section className={styles.listWrap}>
                    {templates.map((template) => {
                        const isArchived = template.isActive === 0
                        const isEmpty = template.componentCount === 0
                        const types = Array.isArray(template.componentTypes) ? template.componentTypes : []

                        return (
                            <article
                                key={template.templateIdentifier}
                                className={`${styles.templateCard} ${isArchived ? styles.templateCardArchived : ''}`}
                                onClick={() => goToDetail(template.templateIdentifier, template.templateName)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && goToDetail(template.templateIdentifier, template.templateName)}
                            >
                                <div className={styles.cardMain}>
                                    <div className={styles.cardLeft}>

                                        {/* Name + status badges */}
                                        <div className={styles.cardNameRow}>
                                            <span className={styles.templateName}>{template.templateName}</span>
                                            {isArchived && (
                                                <span className={styles.archivedBadge}>
                                                    <Archive size={11} /> Archived
                                                </span>
                                            )}
                                        </div>

                                        {/* Description */}
                                        {template.templateDescription && (
                                            <p className={styles.templateDesc}>{template.templateDescription}</p>
                                        )}

                                        {/* Component type chips */}
                                        <div className={styles.chipsRow}>
                                            {types.length > 0 ? (
                                                types.map((type) => {
                                                    const meta = TYPE_META[type] || { label: type, color: '#546e7a', bg: '#eceff1' }
                                                    return (
                                                        <span
                                                            key={type}
                                                            className={styles.typeChip}
                                                            style={{ color: meta.color, background: meta.bg }}
                                                        >
                                                            {meta.label}
                                                        </span>
                                                    )
                                                })
                                            ) : (
                                                <span className={styles.noRulesChip}>No rules added</span>
                                            )}
                                        </div>

                                        {/* Stats */}
                                        <div className={styles.statsRow}>
                                            <span className={styles.statItem}>
                                                <Zap size={12} />
                                                {template.componentCount} rule{template.componentCount !== 1 ? 's' : ''}
                                            </span>
                                            <span className={styles.statDot} />
                                            <span className={styles.statItem}>
                                                <Users size={12} />
                                                {template.staffAssignedCount} staff
                                            </span>
                                        </div>

                                        {/* Warning — no rules */}
                                        {isEmpty && (
                                            <div className={styles.warnBadge}>
                                                <AlertTriangle size={12} />
                                                No commission rules — staff on this plan earn ₹0 variable pay
                                            </div>
                                        )}
                                    </div>

                                    <ChevronRight size={18} className={styles.cardChevron} />
                                </div>
                            </article>
                        )
                    })}
                </section>
            )}

            {/* ── FAB ── */}
            {!isLoading && (
                <button type="button" className={styles.fab} onClick={goToCreate}>
                    <Plus size={17} /> Create Plan
                </button>
            )}
        </div>
    )
}

export default IncentiveStructureScreen